import { useEffect, useState, useRef, useCallback } from 'react';
import { db } from '../../firebase-config';
import {
  collection, query, where, orderBy, limit, onSnapshot,
  doc, runTransaction, serverTimestamp, setDoc, getDoc,
} from 'firebase/firestore';
import { processJob } from './recorder';

/**
 * useJobLoop
 *
 * Subscribes to `youtubeDownloadJobs` filtered to pending jobs and
 * processes them one at a time. The claim is atomic via Firestore
 * transaction so multiple workers can run safely.
 *
 * Single-job-at-a-time discipline: real-time recording is CPU + battery
 * intensive on a phone, so we never claim a second job while one is in
 * flight. The next pending job is picked up the moment the current one
 * finalises.
 *
 * Returns state for the UI to render: current job, progress, recent jobs.
 */
const RECENT_LIMIT = 8;

export function useJobLoop({ workerId, ownerUid }) {
  const [currentJob, setCurrentJob] = useState(null);
  const [progress, setProgress] = useState(null);
  const [state, setState] = useState('idle'); // idle | scanning | claiming | processing | error
  const [detail, setDetail] = useState('waiting for a job…');
  const [recentJobs, setRecentJobs] = useState([]);
  const inFlightRef = useRef(false);
  const recentSeenRef = useRef(new Set());

  // Subscribe to pending jobs
  useEffect(() => {
    if (!workerId) return;
    const q = query(
      collection(db, 'youtubeDownloadJobs'),
      where('status', '==', 'pending'),
      orderBy('requestedAt', 'asc'),
      limit(5),
    );
    const unsub = onSnapshot(q, async (snap) => {
      if (inFlightRef.current) return; // already busy
      if (snap.empty) {
        setState('idle');
        setDetail('waiting for a job…');
        return;
      }
      // Try to claim the oldest one we haven't already attempted
      for (const docSnap of snap.docs) {
        const jobId = docSnap.id;
        if (recentSeenRef.current.has(jobId)) continue;
        const claimed = await tryClaimJob(jobId, workerId);
        if (claimed) {
          recentSeenRef.current.add(jobId);
          await runJob(jobId, claimed);
          break; // process one at a time
        }
      }
    }, (err) => {
      console.error('[JobLoop] snapshot error:', err);
      setState('error');
      setDetail(err.message || String(err));
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId]);

  const tryClaimJob = useCallback(async (jobId, wid) => {
    setState('claiming');
    setDetail(`claiming ${jobId}…`);
    try {
      const result = await runTransaction(db, async (tx) => {
        const jobRef = doc(db, 'youtubeDownloadJobs', jobId);
        const snap = await tx.get(jobRef);
        if (!snap.exists()) return null;
        const data = snap.data();
        if (data.status !== 'pending' || data.assignedWorkerId) {
          return null; // someone else won, or it moved state
        }
        tx.update(jobRef, {
          status: 'in-progress',
          assignedWorkerId: wid,
          claimedAt: serverTimestamp(),
          attempts: (data.attempts || 0) + 1,
        });
        return { id: jobId, ...data, status: 'in-progress', assignedWorkerId: wid };
      });
      return result;
    } catch (e) {
      console.warn('[JobLoop] claim failed:', e);
      return null;
    }
  }, []);

  const runJob = useCallback(async (jobId, jobData) => {
    inFlightRef.current = true;
    setCurrentJob(jobData);
    setState('processing');
    setDetail(`processing ${jobData.title || jobData.videoId}…`);
    setProgress({ percent: 0, message: 'starting…' });

    const jobRef = doc(db, 'youtubeDownloadJobs', jobId);
    const onProgress = (p) => setProgress(p);

    let result;
    try {
      result = await processJob({
        videoId: jobData.videoId,
        title: jobData.title || '',
        artist: jobData.artist || '',
        coverartUrl: jobData.coverartUrl || '',
        workerId,
        ownerUid,
        onProgress,
      });
    } catch (e) {
      console.error('[JobLoop] processing threw:', e);
      result = { success: false, error: e.message || String(e) };
    }

    try {
      if (result?.success && result.hetznerUrl) {
        await setDoc(jobRef, {
          status: 'ready',
          hetznerUrl: result.hetznerUrl,
          fileSize: result.fileSize || null,
          durationSec: result.durationSec || null,
          completedAt: serverTimestamp(),
        }, { merge: true });
        // Also write the cache entry so future requests skip the worker
        const cacheRef = doc(db, 'youtubeCache', jobData.videoId);
        await setDoc(cacheRef, {
          videoId: jobData.videoId,
          title: jobData.title || '',
          artist: jobData.artist || '',
          coverartUrl: jobData.coverartUrl || '',
          hetznerUrl: result.hetznerUrl,
          fileSize: result.fileSize || 0,
          durationSec: result.durationSec || 0,
          bitrate: result.bitrate || 192000,
          recordedAt: serverTimestamp(),
          recordedBy: ownerUid,
          recordedByChannel: 'phone-worker',
          recordedVia: 'phone-worker',
        }, { merge: true });
      } else {
        await setDoc(jobRef, {
          status: 'failed',
          error: result?.error || 'unknown processing error',
          completedAt: serverTimestamp(),
        }, { merge: true });
      }
    } catch (e) {
      console.error('[JobLoop] could not finalise job doc:', e);
    }

    // Read final doc for the UI's recent-jobs list
    try {
      const finalSnap = await getDoc(jobRef);
      const finalData = finalSnap.data() || {};
      setRecentJobs((prev) => [
        { id: jobId, title: finalData.title || finalData.videoId, status: finalData.status || 'unknown' },
        ...prev,
      ].slice(0, RECENT_LIMIT));
    } catch (_) {}

    setCurrentJob(null);
    setProgress(null);
    setState('idle');
    setDetail('waiting for a job…');
    inFlightRef.current = false;
  }, [workerId, ownerUid]);

  return { state, detail, currentJob, progress, recentJobs };
}
